// widget/src/components/ChatInterface.tsx
import React, { useState, useRef, useEffect } from 'react';
import type { UseChatReturn } from '../hooks/useChat';

interface ChatInterfaceProps {
  chat: UseChatReturn;
  primaryColor: string;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({ chat, primaryColor }) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chat.messages]);

  useEffect(() => {
    // Focus input on mount
    inputRef.current?.focus();

    // Send initial greeting if no messages
    // (useChat hook handles the duplicate guard internally)
    if (chat.messages.length === 0) {
      chat.sendMessage('Hi');
    }
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || chat.isLoading) return;

    chat.sendMessage(inputValue.trim());
    setInputValue('');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages area */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}>
        {chat.messages.map((msg, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}
          >
            <div
              style={{
                maxWidth: '80%',
                padding: '10px 14px',
                borderRadius: msg.role === 'user'
                  ? '16px 16px 4px 16px'
                  : '16px 16px 16px 4px',
                backgroundColor: msg.role === 'user' ? primaryColor : '#f1f5f9',
                color: '#1a1a1a',
                fontSize: '14px',
                lineHeight: '1.5',
                wordBreak: 'break-word',
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {chat.isLoading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <div
              style={{
                padding: '10px 14px',
                borderRadius: '16px 16px 16px 4px',
                backgroundColor: '#f1f5f9',
                display: 'flex',
                gap: '4px',
              }}
            >
              <span className="typing-dot" style={{ animationDelay: '0ms' }}>•</span>
              <span className="typing-dot" style={{ animationDelay: '150ms' }}>•</span>
              <span className="typing-dot" style={{ animationDelay: '300ms' }}>•</span>
            </div>
          </div>
        )}

        {chat.error && (
          <div style={{
            padding: '10px 14px',
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderRadius: '8px',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {chat.error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <form
        onSubmit={handleSubmit}
        style={{
          padding: '12px 16px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          gap: '8px',
        }}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Type a message..."
          disabled={chat.isLoading}
          style={{
            flex: 1,
            padding: '10px 14px',
            border: '1px solid #e5e7eb',
            borderRadius: '24px',
            fontSize: '14px',
            outline: 'none',
          }}
          onFocus={(e) => e.target.style.borderColor = primaryColor}
          onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
        />
        <button
          type="submit"
          disabled={chat.isLoading || !inputValue.trim()}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: inputValue.trim() ? primaryColor : '#e5e7eb',
            border: 'none',
            cursor: inputValue.trim() ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1a1a1a" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>

      {/* CSS for typing animation */}
      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
        .typing-dot {
          display: inline-block;
          color: #666;
          font-size: 20px;
          animation: typingBounce 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default ChatInterface;
